import {Card, DataStatus, FormControl, Separator} from 'platform/components';
import {Grid, GridItem, useDevice} from 'platform/foundation';

import {useWatch} from 'react-hook-form';

import {isNil} from 'ramda';

import {useGetCurrenciesQuery, useGetTenantQuery} from '@dms/api/core';
import {useGetPaymentTypeEnumQuery} from '@dms/api/finance';
import {useGetAuthorizationProfilesQuery} from '@dms/api/security';
import {useGetWarehousesQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {getCustomerName, useGetSupplierCustomersQuery} from '@dms/shared';
import {ReadOnlyField} from '@dms/shared-aftersales';

import {pushGtmEvent} from 'shared/web';

import {DeliveryNoteDocumentPreviewFormDefinition} from '../types/DeliveryNoteDocumentPreviewFormDefinition';

interface BasicInformationReadOnlyProps {
  control: FormControl<DeliveryNoteDocumentPreviewFormDefinition>;
  onEdit: VoidFunction;
}

export function BasicInformationReadOnly(props: BasicInformationReadOnlyProps) {
  const device = useDevice();

  const isMobileScreen = device === 'tablet' || device === 'mobile';

  const basicInformation = useWatch({control: props.control, name: 'basicInformation'});

  const suppliersQuery = useGetSupplierCustomersQuery();

  const warehousesQuery = useGetWarehousesQuery();

  const tenantQuery = useGetTenantQuery();

  const authorizationProfilesQuery = useGetAuthorizationProfilesQuery(
    {'x-tenant': tenantQuery.data?.id as string},
    {skip: isNil(tenantQuery.data?.id)}
  );

  const paymentTypesQuery = useGetPaymentTypeEnumQuery();

  const currenciesQuery = useGetCurrenciesQuery();

  const isLoading =
    suppliersQuery.isLoading ||
    warehousesQuery.isLoading ||
    tenantQuery.isLoading ||
    authorizationProfilesQuery.isLoading ||
    paymentTypesQuery.isLoading ||
    currenciesQuery.isLoading;

  const supplier = suppliersQuery.data?.find(
    (supplier) => supplier.id === basicInformation.supplier
  );

  const warehouse = warehousesQuery.data?.find(
    (warehouse) => warehouse.id === basicInformation.warehouse
  );

  const authorizationProfile = authorizationProfilesQuery.data?.find(
    (authorizationProfile) => authorizationProfile.id === basicInformation.authorizationProfile
  );

  const paymentType = paymentTypesQuery.data?.find(
    (paymentType) => paymentType.key === basicInformation.paymentType
  );

  const currency = currenciesQuery.data?.find(
    (currency) => currency.code === basicInformation.currency
  );

  return (
    <Card
      hasSeparator
      isExpandable
      isClosedByDefault={isMobileScreen}
      variant="inlineGrey"
      title={i18n.t('entity.warehouse.labels.basicInformation')}
      actions={[
        {
          type: 'iconButton',
          icon: 'image/edit',
          onClick: () => {
            pushGtmEvent({
              event: 'button_click',
              event_id: 'ai_tool_dn_edit_header',
            });
            props.onEdit();
          },
        },
      ]}
    >
      <DataStatus isLoading={isLoading} minHeight={30}>
        <Grid columns={4}>
          <GridItem span={1}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.supplier')}
              value={getCustomerName(supplier)}
            />
          </GridItem>
          <GridItem span={1}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.warehouse')}
              value={warehouse?.name}
            />
          </GridItem>
          <GridItem span={1}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.deliveryNoteNumber')}
              value={basicInformation.deliveryNoteNumber}
            />
          </GridItem>
          <GridItem span={1}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.invoiceNumber')}
              value={basicInformation.invoiceNumber}
            />
          </GridItem>
        </Grid>

        <Separator />

        <Grid columns={4}>
          <GridItem span={1}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.authorizationProfile')}
              value={authorizationProfile?.name}
            />
          </GridItem>
          <GridItem span={1}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.paymentType')}
              value={paymentType?.value}
            />
          </GridItem>
          <GridItem span={1}>
            <ReadOnlyField
              label={i18n.t('entity.warehouse.labels.currency')}
              value={currency?.name}
            />
          </GridItem>
        </Grid>
      </DataStatus>
    </Card>
  );
}
